import { useMemo, useEffect, useRef, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useSearchParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import {
  BookOpen, ChevronRight, ArrowLeft, MessageCircle,
  Rocket, UserCircle, Route, Map, Bike, Users,
  Camera, Sparkles, CreditCard, Award, Share2, ShieldCheck, LifeBuoy,
  Lightbulb, Info, Search, X, ArrowRight, FileText, Smartphone, Wrench
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HELP_KEY_TO_SLUG, resolveHelpCategory } from "@/lib/help-slugs";

const CATEGORY_META = {
  getting_started: { icon: Rocket,      gradient: "from-green-600 to-emerald-500",  accent: "text-green-400",   border: "border-green-500/30" },
  account:         { icon: UserCircle,   gradient: "from-blue-600 to-sky-500",       accent: "text-blue-400",    border: "border-blue-500/30" },
  trips:           { icon: Route,        gradient: "from-orange-600 to-amber-500",   accent: "text-orange-400",  border: "border-orange-500/30" },
  routes_maps:     { icon: Map,          gradient: "from-teal-600 to-cyan-500",      accent: "text-teal-400",    border: "border-teal-500/30" },
  garage:          { icon: Bike,         gradient: "from-sky-600 to-blue-500",       accent: "text-sky-400",     border: "border-sky-500/30" },
  clubs:           { icon: Users,        gradient: "from-violet-600 to-purple-500",  accent: "text-violet-400",  border: "border-violet-500/30" },
  photos_media:    { icon: Camera,       gradient: "from-pink-600 to-rose-500",      accent: "text-pink-400",    border: "border-pink-500/30" },
  motri:           { icon: Sparkles,     gradient: "from-emerald-600 to-teal-500",   accent: "text-emerald-400", border: "border-emerald-500/30" },
  mototrips_go:    { icon: Smartphone,   gradient: "from-orange-600 to-red-500",     accent: "text-orange-400",  border: "border-orange-500/30" },
  tools:           { icon: Wrench,       gradient: "from-emerald-600 to-green-500",  accent: "text-emerald-400", border: "border-emerald-500/30" },
  plans:           { icon: CreditCard,   gradient: "from-amber-600 to-yellow-500",   accent: "text-amber-400",   border: "border-amber-500/30" },
  ambassador:      { icon: Award,        gradient: "from-fuchsia-600 to-pink-500",   accent: "text-fuchsia-400", border: "border-fuchsia-500/30" },
  social:          { icon: Share2,       gradient: "from-indigo-600 to-violet-500",  accent: "text-indigo-400",  border: "border-indigo-500/30" },
  security:        { icon: ShieldCheck,  gradient: "from-red-600 to-rose-500",       accent: "text-red-400",     border: "border-red-500/30" },
  troubleshooting: { icon: LifeBuoy,     gradient: "from-slate-600 to-zinc-500",     accent: "text-slate-400",   border: "border-slate-500/30" },
};

const CATEGORIES = Object.keys(CATEGORY_META);

function openTawk() {
  if (window.Tawk_API?.maximize) {
    try {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
    } catch {}
  }
}

/** Parse inline markdown: **bold** and [text](url), with nesting support */
function RichText({ text }) {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    let first = null;
    let matchIdx = Infinity;
    if (boldMatch && boldMatch.index < matchIdx) { first = "bold"; matchIdx = boldMatch.index; }
    if (linkMatch && linkMatch.index < matchIdx) { first = "link"; matchIdx = linkMatch.index; }
    if (!first) { parts.push(<Fragment key={key}>{remaining}</Fragment>); break; }
    if (matchIdx > 0) parts.push(<Fragment key={key++}>{remaining.slice(0, matchIdx)}</Fragment>);
    if (first === "bold") {
      parts.push(<strong key={key++} className="text-foreground font-semibold"><RichText text={boldMatch[1]} /></strong>);
      remaining = remaining.slice(matchIdx + boldMatch[0].length);
    } else {
      const isExternal = linkMatch[2].startsWith("http");
      parts.push(
        isExternal
          ? <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">{linkMatch[1]}</a>
          : <Link key={key++} to={linkMatch[2]} className="text-primary hover:underline font-medium">{linkMatch[1]}</Link>
      );
      remaining = remaining.slice(matchIdx + linkMatch[0].length);
    }
  }
  return <>{parts}</>;
}

/** Render article body with rich blocks: callouts (> ), tips (TIP: ), separators (---) */
function ArticleBody({ body, accent }) {
  // b99.10.78 (2026-05-23) — Pre-processing pass: split any block that
  // starts with a markdown heading line (`## / ### / #### Title`) into
  // two separate blocks (heading + rest), so the heading renders as
  // `<h4>` independently of what follows (paragraph, list, callout,
  // etc.). Previous fixes only handled the heading case inside
  // "mixed block with list" — paragraphs after a heading (very common
  // pattern: `### Subtitle\nFollowing paragraph...`) fell into the
  // catch-all <p> and rendered as literal "### Subtitle".
  const rawBlocks = body.split("\n\n");
  const blocks = [];
  for (const b of rawBlocks) {
    const t = b.trim();
    const m = t.match(/^(#{2,4})\s+([^\n]+)(\n([\s\S]+))?$/);
    if (m) {
      blocks.push(`${m[1]} ${m[2]}`);
      if (m[4]) blocks.push(m[4]);
    } else {
      blocks.push(b);
    }
  }
  return blocks.map((block, idx) => {
    const trimmed = block.trim();
    // Separator
    if (trimmed === "---") return <hr key={idx} className="my-4 border-border/30" />;
    // Image: ![alt](url)
    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      return (
        <div key={idx} className={`${idx > 0 ? "mt-4" : ""} max-w-md rounded-xl overflow-hidden border border-border/30 shadow-sm`}>
          <img src={imgMatch[2]} alt={imgMatch[1]} className="w-full h-auto" loading="lazy" />
          {imgMatch[1] && <p className="text-[11px] text-muted-foreground px-2.5 py-1 bg-card/60">{imgMatch[1]}</p>}
        </div>
      );
    }
    // Callout block (lines starting with > )
    if (trimmed.startsWith("> ")) {
      const text = trimmed.replace(/^> /gm, "");
      return (
        <div key={idx} className={`${idx > 0 ? "mt-4" : ""} flex gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20`}>
          <Info className={`h-4 w-4 shrink-0 mt-0.5 ${accent || "text-primary"}`} />
          <p className="text-sm leading-relaxed"><RichText text={text} /></p>
        </div>
      );
    }
    // Tip block
    if (/^(DICA|TIP|CONSEJO|ASTUCE):/i.test(trimmed)) {
      const text = trimmed.replace(/^(DICA|TIP|CONSEJO|ASTUCE):\s*/i, "");
      return (
        <div key={idx} className={`${idx > 0 ? "mt-4" : ""} flex gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20`}>
          <Lightbulb className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
          <p className="text-sm leading-relaxed"><RichText text={text} /></p>
        </div>
      );
    }
    // Unordered list (lines starting with - )
    const lines = trimmed.split("\n");
    const bulletLines = lines.filter(l => l.trim().startsWith("- "));
    const numberedLines = lines.filter(l => /^\d+\.\s/.test(l.trim()));
    
    // Pure bullet list
    if (bulletLines.length > 1 && bulletLines.length === lines.length) {
      return (
        <ul key={idx} className={`${idx > 0 ? "mt-3" : ""} space-y-1.5`}>
          {lines.map((l, li) => (
            <li key={li} className="flex gap-2 text-sm leading-relaxed">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${accent ? accent.replace("text-", "bg-") : "bg-primary"} shrink-0`} />
              <span className="flex-1"><RichText text={l.trim().slice(2)} /></span>
            </li>
          ))}
        </ul>
      );
    }
    // Pure numbered list
    if (numberedLines.length > 1 && numberedLines.length === lines.length) {
      return (
        <ol key={idx} className={`${idx > 0 ? "mt-3" : ""} space-y-1.5`}>
          {lines.map((l, li) => (
            <li key={li} className="flex gap-2 text-sm leading-relaxed">
              <span className={`font-bold ${accent || "text-primary"} shrink-0 w-5 text-right`}>{li + 1}.</span>
              <span className="flex-1"><RichText text={l.trim().replace(/^\d+\.\s*/, "")} /></span>
            </li>
          ))}
        </ol>
      );
    }
    // Mixed block: heading line + bullet/numbered list below
    if (lines.length > 1 && (bulletLines.length > 0 || numberedLines.length > 0)) {
      return (
        <div key={idx} className={idx > 0 ? "mt-3" : ""}>
          {lines.map((l, li) => {
            const t = l.trim();
            // b99.10.76 — markdown heading inside a mixed block.
            if (/^#{2,4}\s+/.test(t)) {
              return (
                <h4
                  key={li}
                  className={`${li > 0 ? "mt-3" : ""} text-sm font-semibold ${accent || "text-primary"}`}
                >
                  <RichText text={t.replace(/^#{2,4}\s+/, "")} />
                </h4>
              );
            }
            if (t.startsWith("- ")) {
              return (
                <div key={li} className="flex gap-2 text-sm leading-relaxed mt-1.5">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${accent ? accent.replace("text-", "bg-") : "bg-primary"} shrink-0`} />
                  <span className="flex-1"><RichText text={t.slice(2)} /></span>
                </div>
              );
            }
            if (/^\d+\.\s/.test(t)) {
              return (
                <div key={li} className="flex gap-2 text-sm leading-relaxed mt-1.5">
                  <span className={`font-bold ${accent || "text-primary"} shrink-0 w-5 text-right`}>{t.match(/^(\d+)\./)[1]}.</span>
                  <span className="flex-1"><RichText text={t.replace(/^\d+\.\s*/, "")} /></span>
                </div>
              );
            }
            return <p key={li} className={li > 0 ? "mt-2" : ""}><RichText text={t} /></p>;
          })}
        </div>
      );
    }
    // Heading line (### Subtitle) — b99.10.76: previously rendered
    // as a literal "### text" paragraph because the parser ignored
    // markdown headings. Now treated as a subsection title scoped to
    // the article. Single line only (no continuation paragraph in
    // the same block).
    if (lines.length === 1 && /^#{2,4}\s+/.test(trimmed)) {
      const text = trimmed.replace(/^#{2,4}\s+/, "");
      return (
        <h4
          key={idx}
          className={`${idx > 0 ? "mt-4" : ""} text-sm font-semibold ${accent || "text-primary"}`}
        >
          <RichText text={text} />
        </h4>
      );
    }
    // Regular paragraph
    return (
      <p key={idx} className={idx > 0 ? "mt-3" : ""}>
        <RichText text={trimmed} />
      </p>
    );
  });
}

export default function HelpCategory() {
  const { t } = useTranslation();
  const { category: urlSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve URL slug (PT, hyphenated) → internal i18n key (English).
  // Old EN-underscored URLs need to redirect to the canonical PT URL —
  // we compute that intent here, but the actual <Navigate /> is rendered
  // at the bottom of the component to satisfy React's rules of hooks
  // (no conditional early-returns BEFORE other hooks have run).
  const resolution = resolveHelpCategory(urlSlug, location.search);
  const category = resolution.kind === "ok" ? resolution.key : null;

  const meta = category ? CATEGORY_META[category] : null;
  const Icon = meta?.icon || BookOpen;

  const [searchParams] = useSearchParams();
  const targetArticle = searchParams.get("artigo");
  const articleRef = useRef(null);
  const [search, setSearch] = useState("");

  // Load articles from i18n
  const articles = useMemo(() => {
    if (!category) return [];
    const data = t(`pages.help_center.articles.${category}`, { returnObjects: true });
    if (!data || typeof data !== "object") return [];
    const result = [];
    let i = 1;
    while (data[`a${i}`]) {
      result.push({ id: `a${i}`, ...data[`a${i}`] });
      i++;
    }
    return result;
  }, [t, category]);

  // Global search across ALL categories
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    const results = [];
    CATEGORIES.forEach((key) => {
      const data = t(`pages.help_center.articles.${key}`, { returnObjects: true });
      if (!data || typeof data !== "object") return;
      let i = 1;
      while (data[`a${i}`]) {
        const article = data[`a${i}`];
        if (article.title?.toLowerCase().includes(q) || article.body?.toLowerCase().includes(q)) {
          results.push({ categoryKey: key, articleId: `a${i}`, title: article.title });
        }
        i++;
      }
    });
    return results;
  }, [search, t]);

  const categoryTitle = t(`pages.help_center.categories.${category}.title`);

  useEffect(() => {
    if (targetArticle && articleRef.current) {
      setTimeout(() => {
        articleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [targetArticle]);

  // Build FAQ Schema for SEO rich snippets
  const faqSchema = useMemo(() => {
    if (!articles.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": articles.map((a) => ({
        "@type": "Question",
        "name": a.title,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": a.body,
        },
      })),
    };
  }, [articles]);

  // Now that all hooks have run, we can safely short-circuit. Old EN slug
  // (e.g. `/ajuda/getting_started`) → 301-equivalent client redirect to
  // the canonical PT URL preserving any query string.
  if (resolution.kind === "redirect") {
    return <Navigate to={resolution.to} replace />;
  }

  if (!meta) {
    return (
      <div className="min-h-screen hero-background pt-36 flex flex-col" data-testid="help-category-not-found">
        <AppHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">{t("pages.help_center.category_not_found")}</p>
            <Link to="/ajuda" className="text-orange-400 hover:text-orange-300 font-medium">
              &larr; {t("pages.help_center.back_to_categories")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-background pt-36 flex flex-col" data-testid="help-category-page">
      <SEO
        title={`${categoryTitle} — ${t("pages.help_center.title")}`}
        description={t(`pages.help_center.categories.${category}.description`)}
        path={`/ajuda/${HELP_KEY_TO_SLUG[category]}`}
        jsonLd={faqSchema}
      />
      <AppHeader />

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 flex-1 bg-card/85 backdrop-blur-sm rounded-xl border border-border/60 shadow-xl my-4">
        {/* Toolbar: breadcrumb + section dropdown + search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
          {/* Left: breadcrumb + dropdown */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Link to="/ajuda" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 shrink-0" data-testid="help-breadcrumb-back">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t("pages.help_center.title")}</span>
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors truncate" data-testid="help-section-dropdown">
                  <Icon className={`h-4 w-4 ${meta.accent} shrink-0`} />
                  <span className="truncate">{categoryTitle}</span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground rotate-90 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto z-[10000]">
                {CATEGORIES.map((key) => {
                  const catMeta = CATEGORY_META[key];
                  const CatIcon = catMeta.icon;
                  const isCurrent = key === category;
                  return (
                    <DropdownMenuItem
                      key={key}
                      onSelect={() => navigate(`/ajuda/${HELP_KEY_TO_SLUG[key]}`)}
                      className={`gap-2 ${isCurrent ? "bg-primary/10 font-medium" : ""}`}
                      data-testid={`help-nav-${key}`}
                    >
                      <CatIcon className={`h-4 w-4 ${isCurrent ? catMeta.accent : "text-muted-foreground"}`} />
                      {t(`pages.help_center.categories.${key}.title`)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right: search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("pages.help_center.search_placeholder")}
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-border/60 bg-card/80 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/50 transition-all"
              data-testid="help-category-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Search results dropdown */}
            {search.trim().length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                {searchResults.length > 0 ? searchResults.slice(0, 8).map((r) => {
                  const rMeta = CATEGORY_META[r.categoryKey];
                  const RIcon = rMeta?.icon || FileText;
                  return (
                    <Link
                      key={`${r.categoryKey}-${r.articleId}`}
                      to={`/ajuda/${HELP_KEY_TO_SLUG[r.categoryKey]}?artigo=${r.articleId}`}
                      onClick={() => setSearch("")}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors no-underline text-foreground"
                    >
                      <RIcon className={`h-3.5 w-3.5 shrink-0 ${rMeta?.accent || "text-muted-foreground"}`} />
                      <span className="truncate flex-1">{r.title}</span>
                      {r.categoryKey !== category && (
                        <span className="text-[10px] text-muted-foreground shrink-0">{t(`pages.help_center.categories.${r.categoryKey}.title`)}</span>
                      )}
                    </Link>
                  );
                }) : (
                  <p className="px-3 py-3 text-sm text-muted-foreground text-center">{t("pages.help_center.no_results")}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Category Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${meta.gradient} shadow-lg`}>
            <Icon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
              {categoryTitle}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t("pages.help_center.articles_in_category", { count: articles.length })}
            </p>
          </div>
        </div>

        {/* Articles */}
        {articles.length > 0 ? (
          <div className={`rounded-xl border overflow-hidden ${meta.border} bg-card/80 backdrop-blur-sm`} data-testid="help-articles-list">
            <Accordion type="single" collapsible defaultValue={targetArticle || undefined} className="px-6 py-2">
              {articles.map((article) => (
                <AccordionItem
                  key={article.id}
                  value={article.id}
                  className="border-border/30"
                  ref={article.id === targetArticle ? articleRef : undefined}
                >
                  <AccordionTrigger
                    className="text-left hover:no-underline py-5 text-sm sm:text-base font-semibold"
                    data-testid={`help-article-${article.id}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${meta.accent}`} />
                      {article.title}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed pl-6 pb-5">
                    <ArticleBody body={article.body} accent={meta.accent} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ) : (
          <div className="text-center py-16 bg-card/60 backdrop-blur-sm rounded-xl border border-border/30" data-testid="help-no-articles">
            <BookOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">{t("pages.help_center.no_articles")}</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12 pt-8 border-t border-border/20">
          <p className="text-muted-foreground text-sm mb-3">
            {t("pages.help_center.cta_text")}
          </p>
          <button
            onClick={openTawk}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
            data-testid="help-category-cta"
          >
            <MessageCircle className="h-4 w-4" />
            {t("pages.help_center.cta_button")}
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
